/**
 * R8 Queue tab — GDELT batch-queue rollup + cost-transparency widget.
 *
 * Honesty rails (CD R8):
 *   - "throttled ≠ broken" — the anonymous-tier cost widget is a STATE,
 *     not an alarm
 *   - Per-source rollup (not per-job firehose) — operator scans the
 *     summary, expands only the row with dead-letters
 *   - Dead-lettered jobs are the only state that REQUIRES a decision
 */

import { useState } from 'react';
import { QueueVerbBtn } from './primitives';
import { SAMPLE_QUEUE, SAMPLE_COST } from './sample-data';

export function QueueView() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const tot = SAMPLE_QUEUE.reduce(
    (a, q) => ({
      running: a.running + q.running,
      queued: a.queued + q.queued,
      done: a.done + q.done,
      failed: a.failed + q.failed,
      dead: a.dead + q.dead,
    }),
    { running: 0, queued: 0, done: 0, failed: 0, dead: 0 },
  );

  const STATS: ReadonlyArray<[string, number, string]> = [
    ['running',     tot.running, 'var(--bm-q-running)'],
    ['queued',      tot.queued,  'var(--bm-q-queued)'],
    ['done',        tot.done,    'var(--bm-q-done)'],
    ['failed',      tot.failed,  'var(--bm-q-failed)'],
    ['dead-letter', tot.dead,    'var(--bm-q-dead)'],
  ];

  return (
    <div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 360px)',
        gap: 16, alignItems: 'start', marginBottom: 14,
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8,
        }}>
          {STATS.map(([label, n, color]) => (
            <div key={label} style={{
              padding: '10px 12px',
              background: 'var(--bm-bg-subtle)',
              border: '1px solid var(--bm-border)',
              borderRadius: 'var(--bm-radius-sm)',
            }}>
              <div style={{
                fontFamily: 'var(--bm-font-mono)', fontSize: 18, fontWeight: 700,
                color,
              }}>{n.toLocaleString()}</div>
              <div style={{
                fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 600,
                letterSpacing: '.4px', textTransform: 'uppercase',
                color: 'var(--bm-text-subtle)', marginTop: 2,
              }}>{label}</div>
            </div>
          ))}
        </div>
        <CostWidget />
      </div>

      <section style={{
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
        overflow: 'hidden',
      }}>
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px',
          borderBottom: '1px solid var(--bm-border)',
        }}>
          <span style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 700,
            letterSpacing: '.4px', color: 'var(--bm-text)',
          }}>GDELT ingest queue · per-source rollup</span>
          <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)' }}>
            job = source × window × attempt · expand for detail
          </span>
        </header>
        {SAMPLE_QUEUE.map(q => {
          const open = expanded === q.id;
          const isDead = q.dead > 0;
          const borderLeft = isDead
            ? 'var(--bm-q-dead)'
            : q.running > 0 ? 'var(--bm-q-running)' : 'var(--bm-border)';
          return (
            <div key={q.id}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1.7fr) 70px 150px',
                gap: 10, alignItems: 'center',
                padding: '7px 12px',
                borderLeft: `3px solid ${borderLeft}`,
                borderBottom: '1px solid var(--bm-border)',
                background: open ? 'var(--bm-bg)' : 'transparent',
                fontSize: 12, lineHeight: 1.3,
              }}>
                <button
                  type="button"
                  onClick={() => setExpanded(open ? null : q.id)}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    textAlign: 'left' as const, padding: 0,
                    fontFamily: 'var(--bm-font-mono)', fontSize: 12, fontWeight: 600,
                    color: 'var(--bm-text)',
                  }}
                >{open ? '▾' : '▸'} {q.source}</button>
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, fontFamily: 'var(--bm-font-mono)', fontSize: 11 }}>
                  {q.running > 0 && <Pill color="var(--bm-q-running)">● {q.running} running</Pill>}
                  {q.queued  > 0 && <Pill color="var(--bm-text-subtle)">{q.queued} queued</Pill>}
                  <Pill color="var(--bm-q-done)">{q.done} done</Pill>
                  {q.failed > 0 && <Pill color="var(--bm-q-failed)">{q.failed} failed</Pill>}
                  {q.dead   > 0 && <Pill color="var(--bm-q-dead)">🔒 {q.dead} dead</Pill>}
                </span>
                <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, textAlign: 'right' as const, color: 'var(--bm-text-muted)' }}>
                  {q.etaMin === null
                    ? <span style={{ color: 'var(--bm-q-dead)' }}>no ETA</span>
                    : q.etaMin === 0 ? 'complete' : `~${q.etaMin}m`}
                </span>
                <span style={{ display: 'inline-flex', justifyContent: 'flex-end', gap: 4 }}>
                  <QueueVerbBtn icon="⏸" label="Pause" />
                  <QueueVerbBtn icon="▶" label="Resume" />
                  <QueueVerbBtn icon="↻" label="Retry failed" />
                  <QueueVerbBtn icon="▲" label="Boost priority" />
                  {isDead && <QueueVerbBtn icon="⤓" label="Defer dead-letter" />}
                </span>
              </div>
              {open && (
                <div style={{
                  padding: '10px 14px',
                  background: 'var(--bm-bg)',
                  borderBottom: '1px solid var(--bm-border)',
                  fontSize: 12, lineHeight: 1.5,
                }}>
                  {isDead ? (
                    <div style={{ color: 'var(--bm-q-dead)' }}>
                      🔒 Dead-lettered after 3 attempts — {q.deadNote}
                    </div>
                  ) : (
                    <div style={{ color: 'var(--bm-text-muted)' }}>
                      {q.done + q.running + q.queued + q.failed} jobs · {q.articles.toLocaleString()} articles ingested · slow but progressing.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </section>

      <div style={{
        marginTop: 10, padding: '10px 14px',
        fontSize: 12, color: 'var(--bm-text-subtle)',
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-sm)',
      }}>
        Honesty rail: <b style={{ color: 'var(--bm-state-stale)' }}>throttled ≠ broken</b>.
        Queued / running jobs are progressing; only <b style={{ color: 'var(--bm-q-dead)' }}>dead-lettered</b> jobs need an operator decision.
      </div>
    </div>
  );
}

function Pill({ color, children }: { color: string; children: React.ReactNode }) {
  return <span style={{ color }}>{children}</span>;
}

function CostWidget() {
  const C = SAMPLE_COST;
  if (C.tier === 'anonymous') {
    return (
      <div style={{
        padding: '10px 12px',
        background: 'var(--bm-prov-stale-bg)',
        border: '1px solid var(--bm-state-stale)',
        borderRadius: 'var(--bm-radius-sm)',
      }}>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
          letterSpacing: '.4px', color: 'var(--bm-state-stale)', marginBottom: 4,
        }}>⏳ ANONYMOUS TIER · throttled</div>
        <div style={{ fontSize: 12, color: 'var(--bm-text)', lineHeight: 1.4, marginBottom: 4 }}>
          No spend — rate-limited, not billed.{' '}
          <b>~{C.paidProjection > 0 ? '$' + C.paidProjection : '—'}/mo</b> projected if upgraded.
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--bm-text-subtle)' }}>
          Ingest is <b style={{ color: 'var(--bm-state-stale)' }}>slow but working</b> — not broken. Upgrade removes the window cap.
        </div>
      </div>
    );
  }
  const pct = Math.min(100, (C.monthSpend / C.monthBudget) * 100);
  const tone: 'ok' | 'warn' | 'over' = pct > 100 ? 'over' : pct > 80 ? 'warn' : 'ok';
  return (
    <div style={{
      padding: '10px 12px',
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-sm)',
    }}>
      <div style={{ display: 'flex', gap: 18, marginBottom: 8 }}>
        <div>
          <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 17, fontWeight: 700 }}>${C.todaySpend}</div>
          <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 600, color: 'var(--bm-text-subtle)', textTransform: 'uppercase', letterSpacing: '.4px' }}>today</div>
        </div>
        <div>
          <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 17, fontWeight: 700 }}>
            ${C.monthSpend}<span style={{ color: 'var(--bm-text-subtle)', fontWeight: 400, fontSize: 12 }}> / ${C.monthBudget}</span>
          </div>
          <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 600, color: 'var(--bm-text-subtle)', textTransform: 'uppercase', letterSpacing: '.4px' }}>this month</div>
        </div>
      </div>
      <div style={{ height: 6, background: 'var(--bm-bg)', border: '1px solid var(--bm-border)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: `var(--bm-budget-${tone})` }} />
      </div>
      {tone === 'over' && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--bm-budget-over)' }}>
          ⚠ Over budget — ingest continues; review tier or scope.
        </div>
      )}
    </div>
  );
}
